/**
 * Navigation flags utilities for Wealthica2
 *
 * Provides helpers to safely clear callback de-duplication flags
 * used by the callback handler to prevent reprocessing within a TTL.
 */

import { WealthicaStorage } from './storage';
import { logger } from './logger';

export const CALLBACK_PROCESSED_KEY = 'WEALTHICA2_CALLBACK_PROCESSED';
export const CALLBACK_TIME_KEY = 'WEALTHICA2_CALLBACK_TIME';

/**
 * Checks if current location appears to be the OAuth callback route.
 * Prevents clearing flags during an active callback to avoid CSRF issues.
 */
function isOnCallbackPage(): boolean {
  try {
    const pathname = window.location.pathname || '';
    const search = window.location.search || '';
    const hash = window.location.hash || '';
    const hasState = search.includes('state=') || hash.includes('state=');
    return pathname.includes('/investments/connect') && hasState;
  } catch {
    return false;
  }
}

/**
 * Clear the callback de-duplication flags. Safe to call from anywhere
 * except the active callback page (guarded).
 */
export function clearCallbackDedupFlags(): void {
  if (isOnCallbackPage()) {
    logger.warn('wealthica2.navigation-flags', 'Skipped clearing callback flags on callback page');
    return;
  }

  try {
    const processed = WealthicaStorage.getItem(CALLBACK_PROCESSED_KEY);
    const time = WealthicaStorage.getItem(CALLBACK_TIME_KEY);

    if (processed || time) {
      logger.info('wealthica2.navigation-flags', 'Clearing callback de-dup flags', {
        hadProcessed: !!processed,
        hadTime: !!time
      });
    }

    WealthicaStorage.removeItem(CALLBACK_PROCESSED_KEY);
    WealthicaStorage.removeItem(CALLBACK_TIME_KEY);
  } catch (error) {
    logger.error('wealthica2.navigation-flags', 'Failed clearing callback de-dup flags', { error });
  }
}
